import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

const SIDES = ["north", "south", "east", "west"];

/** Ch 25.3 — Auto-derived elevation views. */
export default function ElevationSectionPanel({ projectId }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [side, setSide] = useState("north");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/architecture/floor-plans`);
      setPlans(data.floor_plans || []);
      if (data.floor_plans?.[0]) setPlanId(data.floor_plans[0].id);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  useEffect(() => { if (planId) compute(); /* eslint-disable-next-line */ }, [planId, side]);
  const compute = async () => {
    try {
      const { data } = await api.get(
        `/projects/${projectId}/architecture/floor-plans/${planId}/elevation/${side}`);
      setData(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  // ── File 53: Parametric (project-level, derives windows/doors per floor) ──
  const [parametric, setParametric] = useState(null);
  const computeParametric = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/projects/${projectId}/elevation/${side}`);
      setParametric(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  return (
    <div data-testid="elevation-section" style={{ display: "grid",
            gridTemplateColumns: "300px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Floor plan</div>
        <select data-testid="el-plan" style={inputStyle}
          value={planId} onChange={(e) => setPlanId(e.target.value)}>
          {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ ...label, marginTop: 12 }}>Side</div>
        <div style={{ display: "flex", gap: 4 }}>
          {SIDES.map((s) => (
            <button key={s} data-testid={`el-side-${s}`}
              style={s === side ? btn : btnGhost}
              onClick={() => setSide(s)}>{s}</button>
          ))}
        </div>
        <button data-testid="el-parametric-btn"
          style={{ ...btn, width: "100%", marginTop: 12, background: "#16a34a" }}
          onClick={computeParametric}>Parametric (per floor)</button>
        {parametric && (
          <div data-testid="el-parametric-result" style={{ marginTop: 8, fontSize: 11 }}>
            <div style={{ ...label, marginTop: 4 }}>Parametric Output</div>
            <div style={{ marginTop: 4 }}>
              <div>Side: <strong>{parametric.side}</strong></div>
              <div>W × H: <strong>{parametric.building_width_m} × {parametric.building_height_m} m</strong></div>
              <div>Floors: <strong>{parametric.total_floors}</strong></div>
              <div>Floor-to-floor: <strong>{parametric.floor_height_m} m</strong></div>
            </div>
            <div style={{ marginTop: 6, maxHeight: 200, overflow: "auto" }}>
              {(parametric.floors || []).map((f, i) => (
                <div key={i} data-testid={`el-floor-${i}`} style={{
                  padding: 4, marginBottom: 2,
                  background: "rgba(22,163,74,0.06)", borderRadius: 3,
                  fontSize: 10,
                }}>
                  <strong style={{ color: "#F26B1F" }}>{f.floor}</strong>
                  {" @ "}{f.level_m}m · windows {f.windows.length}
                  {f.doors.length > 0 && ` · doors ${f.doors.length}`}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 6, fontSize: 11, color: "var(--pf-muted)",
                       borderBottom: "1px solid var(--pf-border)" }}>
          {data ? `${side.toUpperCase()} elevation · total height ${data.total_height_m}m · ${data.storeys.length} storeys`
                  : "Select a plan to render elevation"}
        </div>
        {data && (
          <svg data-testid="el-canvas"
                viewBox={`-3 ${-(data.total_height_m + 3)} 25 ${data.total_height_m + 6}`}
                style={{ width: "100%", height: "calc(100% - 32px)", background: "#f5f5f0" }}>
            {/* Ground line */}
            <line x1={-3} y1={0} x2={22} y2={0} stroke="#7a4f1b" strokeWidth={0.05} />
            {/* Building outline */}
            <rect x={0} y={-data.total_height_m} width={20} height={data.total_height_m}
                    fill="#cbd5e1" stroke="#1f2937" strokeWidth={0.1} />
            {/* Storeys */}
            {data.storeys.map((s, i) => (
              <g key={i} data-testid={`el-storey-${i}`}>
                {i > 0 && <line x1={0} y1={-s.level_m} x2={20} y2={-s.level_m}
                                   stroke="#94a3b8" strokeWidth={0.04} />}
                <text x={20.5} y={-s.level_m - s.height_m / 2 + 0.3}
                       fontSize={0.5} fill="#222" fontFamily="monospace">
                  {s.name} ({s.height_m}m)
                </text>
                {/* Window strip approximations */}
                {s.openings.slice(0, 5).map((o, oi) => (
                  <rect key={oi} x={2 + oi * 3.5} y={-s.level_m - s.height_m + 1}
                          width={(o.width_m || 1.2)} height={(o.height_m || 1.2)}
                          fill="#3aa0ff" opacity={0.6} stroke="#1f3a5f" strokeWidth={0.03} />
                ))}
              </g>
            ))}
            {/* Storey level labels */}
            {data.storeys.map((s, i) => (
              <text key={`l-${i}`} x={-2.5} y={-s.level_m + 0.3} fontSize={0.4}
                     fill="#222" fontFamily="monospace">
                +{s.level_m}m
              </text>
            ))}
            {/* Compass rose */}
            <g transform="translate(20, -3)">
              <circle r={0.6} fill="#fff" stroke="#222" strokeWidth={0.04} />
              <text y={-0.2} fontSize={0.3} textAnchor="middle" fill="#222" fontWeight="bold">
                {side === "north" ? "N" : side === "south" ? "S" :
                  side === "east" ? "E" : "W"}
              </text>
            </g>
          </svg>
        )}
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Storey table</div>
        {(data?.storeys || []).map((s, i) => (
          <div key={i} data-testid={`el-row-${i}`}
            style={{ padding: 5, marginTop: 4, fontSize: 11,
                      background: "rgba(255,255,255,0.04)", borderRadius: 3 }}>
            <b>{s.name}</b>
            <div style={{ fontFamily: "monospace", fontSize: 10, color: "#aaa" }}>
              level +{s.level_m}m · h {s.height_m}m · {s.openings.length} window{s.openings.length === 1 ? "" : "s"}
            </div>
          </div>
        ))}
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
        <p style={{ fontSize: 9, color: "#666", marginTop: 12, lineHeight: 1.6 }}>
          Auto-elevation generation walks all floors top to bottom, stacks them
          at their floor heights, and projects window openings onto the chosen face.
          Use this view to verify floor count, fenestration, and overall height
          against bye-laws.
        </p>
      </div>
    </div>
  );
}
